import {
	getMaintenanceHealthStatus,
	getRetryAfterMsFromResponse,
	getRouteFullPath,
	isMaintenanceResponse
} from '~/utils/maintenance'

export default function ({
	app,
	$axios,
	$config,
	store,
	redirect,
	error: appError
}) {
	let maintenanceConfirmationPromise = null

	function confirmBackendMaintenance() {
		if (!maintenanceConfirmationPromise) {
			maintenanceConfirmationPromise = getMaintenanceHealthStatus(
				$axios,
				($config && $config.apiVanityUrl) || ''
			).finally(() => {
				maintenanceConfirmationPromise = null
			})
		}

		return maintenanceConfirmationPromise
	}

    /**
     * Add Access Token to the Authorization header
     * if a user is logged in and it's an API call to 
     * and internal Endpoint
     * 
     * NOTE: withCredentials is enabled to support OAuth flows and impersonation.
     * This may contribute to "HTTP header is larger than 10240 bytes" errors.
     * We mitigate this with cookie cleanup and PostHog localStorage configuration.
     */
    $axios.onRequest(config => {
        // Enable credentials for CORS requests (required for some OAuth flows)
        config.withCredentials = !config.__skipCredentials;
        
        if (
            store.getters['user/loggedIn'] &&
            !config.__skipAuthHeader &&
            !config.url.includes('http')
        ) {
            config.headers.common['Authorization'] = `Bearer ${store.state.user.token}`
        }
        if ( store.state.locale ) {
            config.headers.common['Accept-Language'] = store.state.locale
        }
        return config
    })

    /**
     * Display various error messages depending
     * on the error's HTTP response code
     */
	$axios.onError(async error => {
		const code = parseInt(error.response && error.response.status)
        // Toast ownership convention:
        // If a store/page action handles its own error toast UX, set
        // `__skipGlobalErrorToast: true` on that request to avoid duplicates.
		const suppressGlobalErrorToast = Boolean(
			error.config && error.config.__skipGlobalErrorToast
		)
		const suppressMaintenanceHandling = Boolean(
			error.config && error.config.__skipMaintenanceHandling
		)

		if (isMaintenanceResponse(error)) {
			if (suppressMaintenanceHandling) {
				return Promise.reject(error)
			}

			const health = await confirmBackendMaintenance()

			if (health.status === 'maintenance') {
				store.dispatch('maintenance/activate', {
					intendedRoute: getRouteFullPath(
						app.router && app.router.currentRoute
					),
					retryAfterMs: getRetryAfterMsFromResponse(
						health.response
					)
				})

				return Promise.reject(error)
			}
		}

		const errors =
			error.response && error.response.data && error.response.data.errors
				? error.response.data.errors
				: []

		if (suppressGlobalErrorToast && code !== 401) {
			return Promise.reject(error)
		}

		// Don't process errors if user is not logged in, except for 401 errors
		// which we want to handle specifically to prevent Sentry reporting
        if (!store.getters['user/loggedIn'] && code !== 401) {
            return
        }

		// Authentication error: Unauthorized
		if (code === 401) {
			// Authentication failures are expected during expired sessions and failed
			// login attempts. Keep the request rejected, but exclude the original
			// error from Sentry's unhandled rejection reporting.
			error.__suppressSentry = true

			if (store.getters['user/loggedIn']) {
				try {
					await store.dispatch('user/handleUnauthorized')
				} catch (sessionError) {
					console.error(
						'Failed to recover from an unauthorized session:',
						sessionError
					)
				}

				if (process.server && typeof redirect === 'function') {
					redirect('/auth/login')
				}
			}

			return Promise.reject(error)
		}

        // Payment required (No Active Subscription)
        if (code === 402) {
            store.dispatch('toast/push', {
                title: error.response.data.message,
                type: 'warning'
            })
            return Promise.reject(error)
        }

        // Throttling error: Too many requests
        if (code === 429) {
            store.dispatch('toast/push', {
                title: app.i18n.t('error.429.title'),
                message: app.i18n.t('error.429.message'),
                type: 'danger'
            })
            return Promise.reject(error)
        }

        // Validation error
        if (code === 422 && store.getters['user/loggedIn']) {
            if (error.config && error.config.__suppressValidationToast) {
                if (error.config.__showValidationSummary) {
                    store.dispatch('toast/push', {
                        title: app.i18n.t('validation.please_fix_errors'),
                        message: app.i18n.t('validation.form_has_errors'),
                        type: 'warning'
                    })
                }
                return Promise.reject(error)
            }

            const validationMessages = Object.keys(errors).map(key => errors[key].join(' / '))

            store.dispatch('toast/push', {
                title: error.response.data.message,
                type: 'danger',
                message: validationMessages
            })

            return Promise.reject(error)
        }

        // Authorization error: Forbidden
        // Toast, then reject so callers never run success handlers with
        // `undefined`. Callers using then-only chains should recover via
        // `withHandledHttpFailure`; job edit/view loaders set `__skip403Toast`
        // to suppress the toast and handle the rejection themselves.
        if (code === 403) {
            if (error.config && error.config.__skip403Toast) {
                return Promise.reject(error)
            }

            store.dispatch('toast/push', {
                title: app.i18n.t('error.403.title'),
                message: app.i18n.t('error.403.message'),
                type: 'danger'
            })

            return Promise.reject(error)
        }

        // Not found
        if (code === 404) {
            if (error.config && error.config.__skip404Toast) {
                return Promise.reject(error)
            }

            store.dispatch('toast/push', {
                title: app.i18n.t('error.404.title'),
                message: app.i18n.t('error.404.message'),
                type: 'danger'
            })

            return Promise.reject(error)
        }

        // Server error
        if (code >= 500) {
            store.dispatch('toast/push', {
                title: app.i18n.t('error.500.title'),
                message: error.response.data && error.response.data.message
                    ? error.response.data.message
                    : app.i18n.t('error.500.message'),
                type: 'danger'
            })

            // Sentry.showReportDialog({
            //     user: {
            //         name: store.state.user.fullName,
            //         email: store.state.user.email,
            //     },
            //     errorGeneric: error.response.data.message,
            // });

            return Promise.reject(error)
        }

        // Offline
        if (!window.navigator.onLine) {
            console.error('Network offline, could not make API call')

            return Promise.reject(error)
        }

        // appError({
        //     statusCode: code,
        //     message: error.response && error.response.data
        //         ? error.response.data.message
        //         : app.i18n.t(`error.${code}.message`)
        // })

        // Resolving this async hook would run success handlers with undefined.
        return Promise.reject(error)
    })
}
